/**
 * ACP-891: builds the COR digital-signature payload sent with an approval decision.
 *
 * Styled signatures carry the font variant; drawn signatures send the canvas PNG
 * inline as base64 — entity-manager uploads it server-side to the final signatures
 * bucket. We deliberately do NOT reuse the applicant document-upload flow: that
 * endpoint is owner-scoped (the COR is not the owner) and only accepts uploads for
 * DRAFT/RETURNED apps (locked during COR_REVIEW). Integrity hashes are computed
 * client-side over the attestation text and a stable document context so the signed
 * decision is tamper-evident.
 */

import type { SignatureData } from '../../../components/DigitalSignatureModal';
import { getAttestationDisclaimerText } from '../../../utils/attestation-disclaimer';

export interface CorSignaturePayload {
  attestationTextHash: string;
  canvasFingerprint?: string;
  documentHash: string;
  fontFamily?: string;
  fullName: string;
  signatureImage?: string;
  signatureType: 'styled' | 'drawn';
  signedAt: string;
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const buffer = await crypto.subtle.digest('SHA-256', data);

  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Build the COR signature payload from the adopted signature.
 *
 * @param applicationId - Application being decided
 * @param signatureData - Adopted signature from the DigitalSignatureModal
 * @param documentContext - Data hashed into `documentHash` (e.g. the application
 *   data being signed) — anchors the signature to what the COR saw.
 * @param attestationTitle - Title used to render the attestation disclaimer text
 *   that is hashed into `attestationTextHash`.
 */
export async function buildCorSignaturePayload(
  applicationId: string,
  signatureData: SignatureData,
  documentContext: unknown,
  attestationTitle: string,
): Promise<CorSignaturePayload> {
  const signedAt = signatureData.timestamp || new Date().toISOString();
  const [attestationTextHash, documentHash] = await Promise.all([
    sha256Hex(getAttestationDisclaimerText(attestationTitle)),
    sha256Hex(JSON.stringify({ applicationId, context: documentContext ?? null })),
  ]);

  const base = {
    attestationTextHash,
    canvasFingerprint: signatureData.browserFingerprint,
    documentHash,
    fullName: signatureData.fullName,
    signedAt,
  };

  if (signatureData.type === 'draw' && signatureData.imageData) {
    return { ...base, signatureImage: signatureData.imageData, signatureType: 'drawn' };
  }

  return { ...base, fontFamily: signatureData.fontFamily, signatureType: 'styled' };
}
